// A message's files (ios/Sources/Views/AttachmentViews.swift PostAttachmentsView): pictures as
// tiles that open the viewer, PDFs in the browser's own viewer in a new tab, everything else as a
// file card that downloads. A message THIS tab just sent draws its files from the reader's own copy
// (localFiles) with the upload's progress until the server has them.
import { memo, useState } from 'react';
import { AttachmentKind } from '../../../core/attachmentKind';
import { fileBadge, opensInBrowser } from '../../../core/attachmentStaging';
import type { PostFileRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { mediaUrls } from '../../media';
import { echoFiles, isLocalId, localFile, useLocalFilesVersion } from './localFiles';
import { lightboxItemsAround, openLightbox } from './Lightbox';
import { VoiceNoteCard } from '../voice/VoiceNoteCard';
import './attachments.css';

/**
 * PDFs open IN the browser: the bytes are fetched with the session cookie and handed to a new tab
 * as a typed blob (the server answers PDFs as downloads). The tab is opened in the click itself so
 * no pop-up blocker objects; a blocked tab falls back to the download.
 */
export function openPdf(fileId: string, name: string): void {
  const local = localFile(fileId)?.objectUrl;
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  const fallback = () => {
    const a = document.createElement('a');
    a.href = local ?? mediaUrls.file(fileId, true);
    a.download = name;
    a.click();
  };
  if (!tab) return fallback();
  if (local) {
    tab.location.href = local;
    return;
  }
  fetch(mediaUrls.file(fileId), { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
    .then((blob) => {
      tab.location.href = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    })
    .catch(() => {
      tab.close();
      fallback();
    });
}

export const PostFiles = memo(function PostFiles({ files, postId, author, at }: { files: PostFileRecord[] | undefined; postId: string; author: string; at: number }) {
  const list = files?.length ? files : echoFiles(postId);
  const hasLocal = !!list?.some((f) => localFile(f.fileId));
  useLocalFilesVersion(hasLocal);
  if (!list?.length) return null;
  return (
    <>
      {list.map((f) => {
        const local = localFile(f.fileId);
        const pendingUpload = isLocalId(f.fileId) || (local && local.progress >= 0 && local.progress < 1);
        const width = f.width || local?.width || 0;
        const height = f.height || local?.height || 0;
        const kind = AttachmentKind.classify(f.mimeType || local?.mimeType || '', f.name || local?.name || '', width, height);
        const name = f.name || local?.name || f.fileId;
        if (kind === 'image') {
          // The footprint is reserved from the stored dimensions so nothing jumps when the
          // thumbnail lands (VISUAL-DESIGN: no layout shift); 320×350 is the desktop ceiling.
          const scale = Math.min(1, 320 / Math.max(1, width || 320), 350 / Math.max(1, height || 240));
          const w = Math.max(80, Math.round((width || 320) * scale));
          const h = Math.max(60, Math.round((height || 240) * scale));
          // The PREVIEW, not the thumbnail: Mattermost's thumbnail is a 120×100 box, blurry in a
          // 320 px tile (ImageThumbView draws the conversation-resolution preview too).
          const src = local?.objectUrl ?? mediaUrls.preview(f.fileId);
          const href = local?.objectUrl ?? mediaUrls.preview(f.fileId);
          return (
            <a
              key={f.fileId}
              className={`image-tile lb-tile${pendingUpload ? ' uploading' : ''}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              style={{ width: w, height: h }}
              title={name}
              data-lb-file={f.fileId}
              data-lb-name={name}
              data-lb-author={author}
              data-lb-at={at}
              data-lb-w={width}
              data-lb-h={height}
              data-lb-mime={f.mimeType || local?.mimeType || ''}
              onClick={(e) => {
                // ⌘/Ctrl/middle-click keeps the browser's "open in a new tab".
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                const { items, index } = lightboxItemsAround(e.currentTarget);
                openLightbox(items, index);
              }}
            >
              <TileImage src={src} fallback={mediaUrls.file(f.fileId)} alt={name} w={w} h={h} />
              {pendingUpload && local ? <UploadBar progress={local.progress} label={tf('web.attach.uploading', name)} /> : null}
            </a>
          );
        }
        if (kind === 'voice') {
          // Any audio file is the voice player (features/voice), whoever recorded it.
          const mimeType = f.mimeType || local?.mimeType || '';
          return <VoiceNoteCard key={f.fileId} file={{ fileId: f.fileId, name, mimeType }} uploading={pendingUpload && local ? local.progress : null} />;
        }
        const pdf = opensInBrowser(f.mimeType, name);
        const href = local?.objectUrl ?? (pdf ? mediaUrls.file(f.fileId) : mediaUrls.file(f.fileId, true));
        return (
          <a
            key={f.fileId}
            className={`file-card${pendingUpload ? ' uploading' : ''}`}
            href={isLocalId(f.fileId) && !local?.objectUrl ? undefined : href}
            target="_blank"
            rel="noreferrer"
            download={pdf ? undefined : name}
            title={pdf ? t('web.file.openPdf') : tf('web.file.download', name)}
            onClick={(e) => {
              if (!pdf || isLocalId(f.fileId) || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              openPdf(f.fileId, name);
            }}
          >
            <span className="file-icon file-badge">{fileBadge(name)}</span>
            <span style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
              <div className="meta">{AttachmentKind.sizeLabel(f.size || local?.size || 0)}</div>
            </span>
            <Icon name={pdf ? 'external' : 'download'} size={16} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
            {pendingUpload && local ? <UploadBar progress={local.progress} label={tf('web.attach.uploading', name)} /> : null}
          </a>
        );
      })}
    </>
  );
});

/** A picture without a server preview (GIF, SVG, tiny) falls back to its original, once. */
function TileImage({ src, fallback, alt, w, h }: { src: string; fallback: string; alt: string; w: number; h: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={failed ? fallback : src}
      alt={alt}
      width={w}
      height={h}
      loading="lazy"
      decoding="async"
      draggable={false}
      style={{ width: w, height: h, objectFit: 'cover' }}
      onError={() => !failed && src !== fallback && setFailed(true)}
    />
  );
}

function UploadBar({ progress, label }: { progress: number; label: string }) {
  const pct = Math.round(Math.max(0, progress) * 100);
  return (
    <span className="tile-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
      <i style={{ width: `${Math.max(4, pct)}%` }} />
    </span>
  );
}
